import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  PortalMessageApiError,
  portalMemberMessageService,
} from "@/api/portalMemberMessageService";
import {
  clearInboxUnreadCount,
  getCachedConversation,
  getCachedThreadMember,
  applyPortalMessageChangedEvent,
  removeOptimisticThreadMessage,
  upsertInboxConversation,
  upsertThreadFromMessage,
} from "@/hooks/portalMessages/portalMessageCache";
import { portalMessageQueryKeys } from "@/hooks/portalMessages/portalMessageQueryKeys";
import type {
  PortalInboxParams,
  PortalMessage,
  PortalMessageAvatar,
  PortalThreadParams,
  PortalThreadResponse,
  SendPortalMessagePayload,
} from "@/types/portalMessages";

const DEFAULT_INBOX_PARAMS: Required<PortalInboxParams> = {
  page: 1,
  limit: 50,
  search: "",
};

const DEFAULT_THREAD_PARAMS: Required<PortalThreadParams> = {
  limit: 20,
  cursor: null,
  page: 1,
};

const EMPTY_AVATAR: PortalMessageAvatar = {
  url: null,
  publicId: null,
};

export function usePortalMessageConversations(params: PortalInboxParams = {}) {
  const mergedParams = { ...DEFAULT_INBOX_PARAMS, ...params };

  return useQuery({
    queryKey: portalMessageQueryKeys.conversationList(mergedParams),
    queryFn: () => portalMemberMessageService.fetchInbox(mergedParams),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePortalMessagesUnreadCount(enabled = true) {
  return useQuery({
    queryKey: portalMessageQueryKeys.unreadCount(),
    queryFn: portalMemberMessageService.fetchUnreadCount,
    enabled,
    staleTime: 30_000,
  });
}

export function usePortalMessageThread(
  memberId: string | null,
  params: PortalThreadParams = {},
) {
  const mergedParams = { ...DEFAULT_THREAD_PARAMS, ...params };

  return useInfiniteQuery({
    queryKey: memberId
      ? portalMessageQueryKeys.threadCursor(memberId, {
          limit: mergedParams.limit,
        })
      : [...portalMessageQueryKeys.threads, "idle"] as const,
    queryFn: ({ pageParam }) =>
      portalMemberMessageService.fetchThread(memberId!, {
        limit: mergedParams.limit,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.paging?.hasMore ? lastPage.paging.nextCursor : undefined,
    select: (data: InfiniteData<PortalThreadResponse, string | null>) => {
      const firstPage = data.pages[0];
      const orderedPages = [...data.pages].reverse();
      const member =
        data.pages.find((page) => page.member)?.member ?? firstPage?.member;
      const conversationId =
        data.pages.find((page) => page.conversationId)?.conversationId ?? null;
      const paging = data.pages[data.pages.length - 1]?.paging ?? {
        hasMore: false,
        nextCursor: null,
      };

      return {
        ...data,
        messages: orderedPages.flatMap((page) => page.messages),
        paging,
        conversationId,
        member,
      };
    },
    enabled: Boolean(memberId),
    staleTime: 15_000,
  });
}

export function useSendPortalMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      payload,
    }: {
      memberId: string;
      payload: SendPortalMessagePayload;
      replyPreview?: PortalMessage["replyPreview"];
    }) => portalMemberMessageService.sendMessage(memberId, payload),
    onMutate: async ({ memberId, payload, replyPreview }) => {
      const clientMessageId = payload.clientMessageId;

      if (!clientMessageId) {
        return null;
      }

      await queryClient.cancelQueries({
        queryKey: portalMessageQueryKeys.thread(memberId),
      });

      const cachedThreadMember = getCachedThreadMember(queryClient, memberId);
      const cachedConversation = getCachedConversation(queryClient, memberId);
      const createdAt = new Date().toISOString();
      const optimisticMessage: PortalMessage = {
        _id: `optimistic-${clientMessageId}`,
        conversationId: cachedConversation?.conversationId ?? "",
        conversationType: "gym",
        senderId: null,
        receiverId: memberId,
        fitnessCenterId: "",
        memberId,
        senderType: "staff",
        senderUserId: null,
        senderStaffId: null,
        senderNameSnapshot: "Staff",
        text: payload.text,
        createdAt,
        updatedAt: createdAt,
        replyToMessageId: payload.replyToMessageId ?? null,
        replyPreview: replyPreview ?? null,
        clientMessageId,
        isOptimistic: true,
      };

      upsertThreadFromMessage(
        queryClient,
        memberId,
        optimisticMessage,
        cachedThreadMember,
      );
      upsertInboxConversation(queryClient, {
        conversationId: cachedConversation?.conversationId ?? "",
        memberId,
        name: cachedConversation?.name ?? cachedThreadMember?.name ?? "Member",
        avatar:
          cachedConversation?.avatar ?? cachedThreadMember?.avatar ?? EMPTY_AVATAR,
        subscriptionStatus:
          cachedConversation?.subscriptionStatus ??
          cachedThreadMember?.subscriptionStatus ??
          null,
        lastMessage: payload.text,
        lastMessageAt: createdAt,
        unreadCount: 0,
      });

      return { clientMessageId };
    },
    onSuccess: (response, { memberId }) => {
      const cachedThreadMember = getCachedThreadMember(queryClient, memberId);
      const cachedConversation = getCachedConversation(queryClient, memberId);

      upsertThreadFromMessage(
        queryClient,
        memberId,
        response.message,
        cachedThreadMember,
      );
      clearInboxUnreadCount(queryClient, memberId);
      upsertInboxConversation(queryClient, {
        conversationId: response.conversation._id,
        memberId,
        name: cachedConversation?.name ?? cachedThreadMember?.name ?? "Member",
        avatar: cachedConversation?.avatar ?? cachedThreadMember?.avatar ?? EMPTY_AVATAR,
        subscriptionStatus: cachedConversation?.subscriptionStatus ?? null,
        lastMessage: response.message.text,
        lastMessageAt: response.message.createdAt,
        unreadCount: 0,
      });
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.conversations,
      });
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.unreadCount(),
      });
    },
    onError: (_error, { memberId }, context) => {
      if (context?.clientMessageId) {
        removeOptimisticThreadMessage(
          queryClient,
          memberId,
          context.clientMessageId,
        );
      }
    },
  });
}

function usePortalMessageChangeMutation(
  action: "edit" | "remove",
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, messageId, text }: {
      memberId: string;
      messageId: string;
      text?: string;
    }) => action === "edit"
      ? portalMemberMessageService.editMessage(memberId, messageId, text ?? "")
      : portalMemberMessageService.removeMessage(memberId, messageId),
    onSuccess: (response, variables) => {
      const result = applyPortalMessageChangedEvent(queryClient, {
        message: response.message,
        revision: response.revision,
        conversation: response.conversation,
        replyPreviewUpdates: {
          targetMessageId: response.message._id,
          text: response.message.text,
        },
      });

      if (result.applied && !result.threadUpdated) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.thread(variables.memberId),
        });
      }
      if (result.applied && !result.conversationUpdated) {
        void queryClient.invalidateQueries({
          queryKey: portalMessageQueryKeys.conversations,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.messageHistory(response.message._id),
      });
    },
    onError: async (error, variables) => {
      const status = error instanceof PortalMessageApiError ? error.status : undefined;
      if (status === 409 || status === 404) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: portalMessageQueryKeys.thread(variables.memberId),
          }),
          queryClient.invalidateQueries({
            queryKey: portalMessageQueryKeys.conversations,
          }),
        ]);
      }
    },
  });
}

export function useEditPortalMessage() {
  return usePortalMessageChangeMutation("edit");
}

export function useRemovePortalMessage() {
  return usePortalMessageChangeMutation("remove");
}

export function usePortalMessageHistory(messageId: string | null) {
  return useQuery({
    queryKey: portalMessageQueryKeys.messageHistory(messageId ?? "idle"),
    queryFn: () => portalMemberMessageService.fetchMessageHistory(messageId!),
    enabled: Boolean(messageId),
    staleTime: 30_000,
  });
}

export function useMarkPortalThreadAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      conversationId,
    }: {
      memberId: string;
      conversationId?: string | null;
    }) => portalMemberMessageService.markThreadAsRead(memberId, conversationId),
    onSuccess: (_response, { memberId }) => {
      clearInboxUnreadCount(queryClient, memberId);
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.conversations,
      });
      void queryClient.invalidateQueries({
        queryKey: portalMessageQueryKeys.unreadCount(),
      });
    },
  });
}
